import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { AccountService } from '../../core/auth/account.service';
import { RoleService as BookingRoleService } from '../../booking/role/role.service';
import { DoorOpenDialogComponent } from '../../entities/locations/devices/door-open-dialog/door-open-dialog.component';
import { FeatureEnum } from '../../shared/booking/constants/feature.constants';
import { FeaturesEnum } from '../../shared/constants/features.constants';
import { IDoorOpenDialogData } from '../../entities/locations/devices/door-open-dialog/door-open-dialog-data';
import { IFeature } from '../../shared/booking/model/feature.model';
import { LANGUAGES } from '../../core/language/language.constants';
import { LocalStorageService } from 'ngx-webstorage';
import { LoginService } from '../../core/login/login.service';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ModuleDto } from '../../core/user/module.model';
import { ProfileService } from '../profiles/profile.service';
import { Roles } from '../../types/enums';
import { TranslateService } from '@ngx-translate/core';
import { ViewChild } from '@angular/core';
import { LocationsService } from '../../entities/locations/locations.service';
import { IDeviceDto } from '../../entities/locations/devices/device-dto';

@Component({
  selector: 'app-navbar',
  standalone: false,
  templateUrl: './navbar.component.html',
  styleUrls: ['navbar.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  inProduction?: boolean;
  isNavbarCollapsed = true;
  private collapseBreakpoint = 1200; // px; adjust to 900 if desired

  isReportsCollapsed = false;
  isIdsCollapsed = false;
  isProfileCollapsed = true;
  isLanguageCollapsed = false;

  public innerWidth: any;
  isMobile = false;

  languages = LANGUAGES;
  swaggerEnabled?: boolean;
  userName = '';
  userNameInitials = '';
  gymName = '';
  newMessages = 0;
  loggedInJhiUserId: string | undefined;

  modules: ModuleDto[] = [];
  benefitPlus = false;
  benefitCorporate = false;
  benefitAccessControl = false;
  bAccessControl = false;
  canOpenDoor = false;
  availableDevices: IDeviceDto[] = [];

  openDoorOpenDialog = false;
  activeLanguage = 'en';

  // Booking start
  benefitBooking = false;
  isBookingCollapsed = false;

  permitedFeatures: IFeature[] = [];

  // Selection flags for parent items with submenus
  isReportsSelected = false;
  isIdsSelected = false;
  isBookingSelected = false;

  // Interval for message counting
  private messageCountInterval?: any;

  // Subscriptions for cleanup
  private subscriptions: any[] = [];

  permitedScheduleAppointment: boolean = false;
  permitedReservation: boolean = false;
  permitedResourceReservationSchedule: boolean = false;
  permitedCustomerReservationSchedule: boolean = false;
  permitedCalendarIndividualResource: boolean = false;
  permitedUser: boolean = false;
  permitedWorkHours: boolean = false;
  permitedResourceOccupancy: boolean = false;
  permitedScheduleIndividualResource: boolean = false;
  permitedResource: boolean = false;
  permitedUtility: boolean = false;
  permitedAsset: boolean = false;
  permitedCustomerStatus: boolean = false;
  permitedSettings: boolean = false;

  showSchedule: boolean = false;
  showView: boolean = false;
  showCodebook: boolean = false;
  showBooking: boolean = false;
  // Booking end

  @Output() eventCollapsed = new EventEmitter<boolean>();

  // version: string;
  checkUser = false;
  availableFeatures: number[] = [];
  FEATURES_ENUM = FeaturesEnum;

  // @ViewChild('scrollMe') private myScrollContainer: ElementRef;

  constructor(
    private loginService: LoginService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private accountService: AccountService,
    private profileService: ProfileService,
    private bookingRoleService: BookingRoleService,
    private router: Router,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private locationsService: LocationsService
  ) {
    // this.version = VERSION ? (VERSION.toLowerCase().startsWith('v') ? VERSION : 'v' + VERSION) : '';
    this.newMessages = this.accountService.newMessages ?? 0;
  }

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
    // Apply initial responsive collapse based on breakpoint
    this.isNavbarCollapsed = this.innerWidth < this.collapseBreakpoint;
    this.eventCollapsed.emit(this.isNavbarCollapsed);

    // Listen to window resize to auto-collapse/expand around breakpoint
    const onResize = () => {
      const w = window.innerWidth;
      const shouldCollapse = w < this.collapseBreakpoint;
      if (shouldCollapse !== this.isNavbarCollapsed) {
        this.isNavbarCollapsed = shouldCollapse;
        this.eventCollapsed.emit(this.isNavbarCollapsed);
        this.cdr.detectChanges();
      }
    };
    window.addEventListener('resize', onResize);
    this.subscriptions.push({ unsubscribe: () => window.removeEventListener('resize', onResize) });
    // Fallback global mouse tracker to robustly close flyouts when cursor leaves both regions
    window.addEventListener('mousemove', this.globalMouseMove);
    this.subscriptions.push({
      unsubscribe: () => window.removeEventListener('mousemove', this.globalMouseMove),
    });

    // Global click handler to close flyouts when clicking anywhere on navbar (except parent items and flyout)
    window.addEventListener('click', this.globalClickHandler);
    this.subscriptions.push({
      unsubscribe: () => window.removeEventListener('click', this.globalClickHandler),
    });
    this.setActiveLanguage();
    this.subscriptions.push(
      this.profileService.getProfileInfo().subscribe((profileInfo) => {
        this.inProduction = profileInfo.inProduction;
        this.swaggerEnabled = profileInfo.swaggerEnabled;
      })
    );

    this.subscriptions.push(
      this.accountService.identity(true).subscribe((res: any) => {
        this.gymName = res?.companyName;
        this.loggedInJhiUserId = res?.id;
        this.availableFeatures = res?.featureIds ? res?.featureIds : [];
        this.bAccessControl = res?.authorities?.includes(Roles.ROLE_ADMIN);
        console.log('User authorities:', res?.authorities);
        console.log('bAccessControl:', this.bAccessControl);
        this.userName = res?.firstName + ' ' + res?.lastName;
        this.userNameInitials = res?.firstName[0] + '. ' + res?.lastName[0] + '.';
        for (let i = 0; i < res?.authorities?.length; i++) {
          if (res?.authorities[i] === Roles.ROLE_ADMIN) {
            this.checkUser = true;
          }
        }
        // Only count unseen messages if technoUserId is valid
        if (res?.technoUserId && !isNaN(Number(res.technoUserId))) {
          this.countUnseenMessages(Number(res.technoUserId));
          // Clear any existing interval before creating a new one
          if (this.messageCountInterval) {
            clearInterval(this.messageCountInterval);
          }
          this.messageCountInterval = setInterval(() => {
            this.countUnseenMessages(Number(res.technoUserId));
          }, 60 * 1000);
        } else {
          console.warn('Invalid technoUserId in navbar:', res?.technoUserId);
        }

        // Only get role features if user is logged in and has a roleId
        if (res && res.roleId) {
          this.bookingRoleService.getAllFeaturesForRole(res.roleId).subscribe((resRole) => {
            this.permitedFeatures = resRole.body || [];
            this.permitedScheduleAppointment = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.SCHEDULE_APPOINTMENT
            )
              ? true
              : false;
            this.permitedReservation = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.RESERVATION
            )
              ? true
              : false;
            this.permitedSettings = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.APP_CONFIGURATION
            )
              ? true
              : false;

            this.permitedResourceReservationSchedule = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.RESOURCE_RESERVATION_SCHEDULE
            )
              ? true
              : false;
            this.permitedCustomerReservationSchedule = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.CUSTOMER_RESERVATION_SCHEDULE
            )
              ? true
              : false;
            this.permitedCalendarIndividualResource = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.CALENDAR_FOR_AN_INDIVIDUAL_RESOURCE
            )
              ? true
              : false;
            this.showSchedule =
              this.permitedResourceReservationSchedule ||
              this.permitedCustomerReservationSchedule ||
              this.permitedCalendarIndividualResource;

            this.permitedUser = this.permitedFeatures.find((el) => el.route === FeatureEnum.USER)
              ? true
              : false;
            this.permitedWorkHours = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.WORK_HOURS
            )
              ? true
              : false;
            this.permitedResourceOccupancy = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.RESOURCE_OCCUPANCY
            )
              ? true
              : false;
            this.permitedScheduleIndividualResource = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.SCHEDULE_FOR_AN_INDIVIDUAL_RESOURCE
            )
              ? true
              : false;
            this.showView =
              this.permitedUser ||
              this.permitedWorkHours ||
              this.permitedResourceOccupancy ||
              this.permitedScheduleIndividualResource;

            this.permitedResource = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.RESOURCE
            )
              ? true
              : false;
            this.permitedUtility = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.UTILITY
            )
              ? true
              : false;
            this.permitedAsset = this.permitedFeatures.find((el) => el.route === FeatureEnum.ASSET)
              ? true
              : false;
            this.permitedCustomerStatus = this.permitedFeatures.find(
              (el) => el.route === FeatureEnum.CUSTOMER_STATUS
            )
              ? true
              : false;
            this.showCodebook =
              this.permitedResource ||
              this.permitedUtility ||
              this.permitedAsset ||
              this.permitedCustomerStatus;

            this.showBooking =
              this.permitedScheduleAppointment ||
              this.permitedReservation ||
              this.permitedResourceReservationSchedule ||
              this.permitedCustomerReservationSchedule ||
              this.permitedCalendarIndividualResource ||
              this.permitedUser ||
              this.permitedWorkHours ||
              this.permitedResourceOccupancy ||
              this.permitedScheduleIndividualResource ||
              this.permitedResource ||
              this.permitedUtility ||
              this.permitedAsset ||
              this.permitedCustomerStatus ||
              this.permitedSettings ||
              this.permitedSettings;
          });

          // Only get active modules if user is logged in
          this.accountService.getActiveModule().subscribe({
            next: (data) => {
              this.modules = data;
              const bp = this.modules.filter((m) => m.id === 1);
              const bc = this.modules.filter((m) => m.id === 2);
              const bb = this.modules.filter((m) => m.id === 3);
              const bac = this.modules.filter((m) => m.id === 2 || m.id === 4 || m.id === 7);
              if (bp.length > 0) this.benefitPlus = true;
              if (bc.length > 0) this.benefitCorporate = true;
              if (bb.length > 0) this.benefitBooking = true;
              if (bac.length > 0) this.benefitAccessControl = true;
              this.evaluateOpenDoorAvailability();
            },
            error: (error) => {
              console.warn('Could not load active modules (user may not be authenticated):', error);
              // Set default module states for unauthenticated users
              this.benefitPlus = false;
              this.benefitCorporate = false;
              this.benefitBooking = false;
              this.benefitAccessControl = false;
              this.canOpenDoor = false;
            },
          });
        }
      })
    );

    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        // Keep small screens collapsed; otherwise leave to breakpoint logic
        if (window.innerWidth < 768) this.isNavbarCollapsed = true;
        if (event instanceof NavigationStart) {
          // Clear all parent selections immediately on navigation start
          this.isReportsSelected = false;
          this.isIdsSelected = false;
          this.isBookingSelected = false;
        }
        if (event instanceof NavigationEnd) {
          // Use setTimeout to defer the update to the next tick, preventing expression change error
          setTimeout(() => {
            this.updateParentSelections(event.urlAfterRedirects || event.url);
            this.cdr.detectChanges();
          }, 0);
        }
      })
    );
    // Initialize selection on first load - defer to prevent expression change error
    setTimeout(() => {
      this.updateParentSelections(this.router.url);
      this.cdr.detectChanges();
    }, 0);
    // jQuery('.collapse').collapse()
  }
  get newMessagesCount(): number {
    return this.accountService.newMessages ?? 0;
  }

  scrollToBottom(): void {
    console.log('Red');
    try {
      setTimeout(() => {
        const objDiv = document.getElementById('scrollElement');
        if (objDiv) {
          objDiv.scrollTop = objDiv.scrollHeight;
        }
      }, 100);
    } catch (err) {
      console.log(err);
    }
  }

  setActiveLanguage(): void {
    this.activeLanguage = this.localStorageService.retrieve('locale');
  }

  changeLanguage(languageKey: string): void {
    this.localStorageService.store('locale', languageKey);
    this.activeLanguage = languageKey.toLocaleLowerCase();
    // this.languageService.changeLanguage(languageKey);
    this.translateService.use(languageKey);
    this.isLanguageCollapsed = false;
    this.accountService.setSessionLanguage(languageKey).subscribe();
  }

  isAuthenticated(): boolean {
    return this.accountService.isAuthenticated();
  }

  login(): void {
    this.router.navigate(['/account/login']);
  }

  logout(): void {
    this.collapseNavbar();
    this.loginService.logout();
    this.router.navigate(['/account/login']);
  }

  // Template triggers for collapsed flyouts
  @ViewChild('bookingTrigger') bookingTrigger?: MatMenuTrigger;
  @ViewChild('idsTrigger') idsTrigger?: MatMenuTrigger;
  @ViewChild('reportsTrigger') reportsTrigger?: MatMenuTrigger;
  @ViewChild('bookingItem') bookingItem?: ElementRef<HTMLElement>;
  @ViewChild('idsItem') idsItem?: ElementRef<HTMLElement>;
  @ViewChild('reportsItem') reportsItem?: ElementRef<HTMLElement>;

  private currentFlyout: 'booking' | 'ids' | 'report' | null = null;

  openSubmenu(type: string): void {
    // In collapsed mode, do not expand the navbar; flyout appears via CSS hover
    if (this.isNavbarCollapsed) {
      return;
    }
    // Determine if clicked parent is already open
    const wasOpen =
      type === 'report'
        ? !this.isReportsCollapsed
        : type === 'ids'
          ? !this.isIdsCollapsed
          : type === 'booking'
            ? !this.isBookingCollapsed
            : type === 'profile'
              ? !this.isProfileCollapsed
              : false;

    if (wasOpen) {
      // If the clicked parent is open, collapse it and clear its selection, do not navigate
      switch (type) {
        case 'report':
          this.isReportsCollapsed = true;
          this.isReportsSelected = false;
          break;
        case 'ids':
          this.isIdsCollapsed = true;
          this.isIdsSelected = false;
          break;
        case 'profile':
          this.isProfileCollapsed = true;
          break;
        case 'booking':
          this.isBookingCollapsed = true;
          this.isBookingSelected = false;
          break;
      }
      return;
    }

    // Opening a parent: clear selections and collapse others first
    this.isReportsSelected = false;
    this.isIdsSelected = false;
    this.isBookingSelected = false;
    this.collapseAllParentMenus();

    switch (type) {
      case 'report':
        this.isReportsCollapsed = false;
        this.expandNavbar();
        this.navigateToFirstAvailableSubmenu('report');
        this.isReportsSelected = true;
        break;
      case 'ids':
        // Only expand/navigate if the user has USER_ADMINISTRATION feature
        if (this.hasIdsAccess()) {
          this.isIdsCollapsed = false;
          this.expandNavbar();
          this.navigateToFirstAvailableSubmenu('ids');
          this.isIdsSelected = true;
        } else {
          try {
            console.debug('[Navbar] IDS blocked: missing USER_ADMINISTRATION');
          } catch {}
        }
        break;
      case 'profile':
        this.isProfileCollapsed = false;
        this.expandNavbar();
        break;
      case 'booking':
        this.isBookingCollapsed = false;
        this.expandNavbar();
        this.navigateToFirstAvailableSubmenu('booking');
        this.isBookingSelected = true;
        break;
    }
  }

  // ===== Collapsed hover flyout helpers =====
  private hoverState: Record<string, { trigger: boolean; menu: boolean }> = {
    booking: { trigger: false, menu: false },
    ids: { trigger: false, menu: false },
    report: { trigger: false, menu: false },
  };
  private closeTimers: Record<string, any> = { booking: null, ids: null, report: null };

  private getTrigger(type: 'booking' | 'ids' | 'report') {
    switch (type) {
      case 'booking':
        return this.bookingTrigger;
      case 'ids':
        return this.idsTrigger;
      case 'report':
        return this.reportsTrigger;
    }
  }

  private closeAllFlyoutsExcept(active: 'booking' | 'ids' | 'report'): void {
    if (!this.isNavbarCollapsed) return;
    (['booking', 'ids', 'report'] as const)
      .filter((t) => t !== active)
      .forEach((t) => {
        // Immediately cancel any pending close timers
        if (this.closeTimers[t]) {
          clearTimeout(this.closeTimers[t]);
          this.closeTimers[t] = null;
        }
        // Force close the menu immediately
        try {
          const trigger = this.getTrigger(t);
          if (trigger && trigger.menuOpen) {
            trigger.closeMenu();
          }
        } catch {}
        // Reset hover state
        this.hoverState[t] = { trigger: false, menu: false };
        // Clear currentFlyout if it was this one
        if (this.currentFlyout === t) {
          this.currentFlyout = null;
        }
      });
  }

  onTriggerEnter(type: 'booking' | 'ids' | 'report'): void {
    if (!this.isNavbarCollapsed) return;
    // ensure only one flyout is visible at a time
    this.closeAllFlyoutsExcept(type);
    this.currentFlyout = type;
    this.hoverState[type].trigger = true;
    if (this.closeTimers[type]) {
      clearTimeout(this.closeTimers[type]);
      this.closeTimers[type] = null;
    }
    const trg = this.getTrigger(type);
    if (trg && !trg.menuOpen) trg.openMenu();
  }
  onTriggerLeave(evt: MouseEvent, type: 'booking' | 'ids' | 'report'): void {
    if (!this.isNavbarCollapsed) return;

    // If leaving into the flyout menu, do not schedule close
    const target = evt.relatedTarget as HTMLElement | null;
    const intoOverlay = !!(
      target &&
      (target.closest('.nav-flyout-menu') ||
        target.closest('.flyout-container') ||
        target.closest('.cdk-overlay-connected-position-bounding-box') ||
        target.closest('.cdk-overlay-pane'))
    );

    if (intoOverlay) {
      // Cursor is moving into flyout menu - don't close
      this.hoverState[type].menu = true;
      this.hoverState[type].trigger = false;
      return;
    }

    // Check if cursor is moving into another parent hover area (prevents blinking)
    const { clientX: x, clientY: y } = evt;
    const allParentItems = ['booking', 'ids', 'report'] as const;
    const enteringAnotherParent = allParentItems.some((t) => {
      if (t === type) return false; // Don't check the current one
      const otherLi = this.getListItemEl(t);
      const otherLiRect = otherLi ? otherLi.getBoundingClientRect() : null;
      return this.pointInRect(x, y, otherLiRect);
    });

    if (enteringAnotherParent) {
      // Cursor is moving to another parent - don't schedule close, let onTriggerEnter handle it
      return;
    }

    // Cursor left parent hover area - start closing immediately with shorter delay
    this.hoverState[type].trigger = false;
    this.scheduleClose(type, 100); // Faster close when leaving parent hover area
  }
  onMenuEnter(type: 'booking' | 'ids' | 'report'): void {
    if (!this.isNavbarCollapsed) return;
    this.hoverState[type].menu = true;
    if (this.closeTimers[type]) {
      clearTimeout(this.closeTimers[type]);
      this.closeTimers[type] = null;
    }
  }
  onMenuLeave(type: 'booking' | 'ids' | 'report'): void {
    if (!this.isNavbarCollapsed) return;
    this.hoverState[type].menu = false;
    this.scheduleClose(type, 100); // Faster close when leaving flyout menu as well
  }
  closeFlyout(type: 'booking' | 'ids' | 'report'): void {
    try {
      this.getTrigger(type)?.closeMenu();
    } catch {}
    this.hoverState[type] = { trigger: false, menu: false };
    if (this.currentFlyout === type) this.currentFlyout = null;
  }
  private maybeClose(type: 'booking' | 'ids' | 'report'): void {
    const hs = this.hoverState[type];
    if (!hs.trigger && !hs.menu) {
      try {
        this.getTrigger(type)?.closeMenu();
      } catch {}
      if (this.currentFlyout === type) this.currentFlyout = null;
    }
  }

  private scheduleClose(type: 'booking' | 'ids' | 'report', delay: number = 220): void {
    if (this.closeTimers[type]) {
      clearTimeout(this.closeTimers[type]);
      this.closeTimers[type] = null;
    }
    this.closeTimers[type] = setTimeout(() => this.maybeClose(type), delay);
  }

  onTriggerClick(type: 'booking' | 'ids' | 'report', event?: MouseEvent): void {
    if (this.isNavbarCollapsed) {
      this.closeAllFlyoutsExcept(type);
      this.getTrigger(type)?.openMenu();
    } else {
      this.openSubmenu(type);
    }
  }

  private collapseAllParentMenus(): void {
    // Collapse all parent menus except the one being opened
    this.isReportsCollapsed = true;
    this.isIdsCollapsed = true;
    this.isBookingCollapsed = true;
    this.isProfileCollapsed = true;
  }

  expandNavbar(): void {
    this.isNavbarCollapsed = false;
    this.eventCollapsed.emit(this.isNavbarCollapsed);
  }

  collapseNavbar(): void {
    this.isNavbarCollapsed = true;
    this.isReportsCollapsed = true;
    this.isIdsCollapsed = true;
    this.isBookingCollapsed = true;
    this.eventCollapsed.emit(this.isNavbarCollapsed);
  }

  getImageUrl(): string {
    return this.isAuthenticated() ? this.accountService.getImageUrl() : '';
  }

  checkModule(moduleId: number): boolean {
    return true;
  }

  countUnseenMessages(userId: number): void {
    if (userId && !isNaN(userId) && userId > 0) {
      this.accountService.fetchUnseenMessages(userId).subscribe();
    } else {
      console.warn('Invalid userId passed to countUnseenMessages:', userId);
    }
  }

  onClickOpenDoor($even: Event): void {
    if (!this.canOpenDoor) return;
    this.openDoorOpenDialog = true;
    const data: IDoorOpenDialogData = {
      location: this.localStorageService.retrieve('location'),
      devices: this.availableDevices,
      userId: this.loggedInJhiUserId,
    };
    const dialogRef = this.dialog.open(DoorOpenDialogComponent, {
      // width: auto (no hard cap); rely on responsive maxWidth
      maxWidth: '90vw',
      maxHeight: '90vh',
      data,
      panelClass: 'door-open-dialog',
    });
    dialogRef.afterClosed().subscribe(() => {
      this.openDoorOpenDialog = false;
    });
  }

  // openDoor(): void {
  //   const locId = this.localStorageService.retrieve('location');
  //   this.deviceService.openDoor(locId, this.loggedInJhiUserId).subscribe(res => {});
  // }

  canAccessFeature(featureId: number): boolean {
    return this.availableFeatures.includes(featureId);
  }

  private updateParentSelections(currentUrl: string): void {
    // First, clear all parent selections to prevent dual pills
    this.isReportsSelected = false;
    this.isIdsSelected = false;
    this.isBookingSelected = false;

    // Normalize URL
    const url = currentUrl || '';
    this.isReportsSelected = this.isUnder(url, '/reports');
    this.isIdsSelected = this.isUnder(url, '/ids');
    this.isBookingSelected = this.isUnder(url, '/booking');

    // If no parent menu is active, collapse all parent menus
    if (!this.isReportsSelected && !this.isIdsSelected && !this.isBookingSelected) {
      this.collapseAllParentMenus();
    }
  }

  private isUnder(url: string, prefix: string): boolean {
    // Matches either exact prefix or any child route under it
    return (
      url === prefix || url.startsWith(prefix + '/') || url.includes(`/${prefix.replace('/', '')}/`)
    );
  }

  isOnRecordsPage(): boolean {
    const currentUrl = this.router.url;
    return this.isUnder(currentUrl, '/records');
  }

  // ===== Global pointer fallback to ensure flyout closes when leaving both trigger and menu =====
  private pointInRect(x: number, y: number, rect: DOMRect | null | undefined): boolean {
    if (!rect) return false;
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  private getListItemEl(type: 'booking' | 'ids' | 'report'): HTMLElement | null {
    switch (type) {
      case 'booking':
        return this.bookingItem?.nativeElement ?? null;
      case 'ids':
        return this.idsItem?.nativeElement ?? null;
      case 'report':
        return this.reportsItem?.nativeElement ?? null;
    }
  }

  private globalMouseMove = (e: MouseEvent) => {
    if (!this.isNavbarCollapsed) return;
    const type = this.currentFlyout;
    if (!type) return;
    const trg = this.getTrigger(type);
    if (!trg || !trg.menuOpen) return;
    const x = e.clientX;
    const y = e.clientY;
    const li = this.getListItemEl(type);
    const liRect = li ? li.getBoundingClientRect() : null;
    const panel = document.querySelector(
      '.cdk-overlay-pane .nav-flyout-menu'
    ) as HTMLElement | null;
    const panelRect = panel ? panel.getBoundingClientRect() : null;
    const overTrigger = this.pointInRect(x, y, liRect);
    const overMenu = this.pointInRect(x, y, panelRect);

    // Check if cursor is entering another parent hover area
    const allParentItems = ['booking', 'ids', 'report'] as const;
    const enteringAnotherParent = allParentItems.some((t) => {
      if (t === type) return false; // Don't check the current one
      const otherLi = this.getListItemEl(t);
      const otherLiRect = otherLi ? otherLi.getBoundingClientRect() : null;
      return this.pointInRect(x, y, otherLiRect);
    });

    if (overTrigger || overMenu || enteringAnotherParent) {
      // cancel any pending close if pointer is within either region or entering another parent
      if (this.closeTimers[type]) {
        clearTimeout(this.closeTimers[type]);
        this.closeTimers[type] = null;
      }
    } else {
      this.scheduleClose(type, 100); // Faster close when cursor leaves both areas
    }
  };

  private globalClickHandler = (e: MouseEvent) => {
    if (!this.isNavbarCollapsed) return;

    const target = e.target as HTMLElement;
    if (!target) return;

    // Check if click is on a parent item or flyout menu - don't close in those cases
    const isOnParentItem = !!(
      target.closest('.dropdown-hover-area') ||
      target.closest('.nav-item.dropdown > a') ||
      target.closest('.nav-item.dropdown')
    );

    const isOnFlyout = !!(
      target.closest('.nav-flyout-menu') ||
      target.closest('.flyout-container') ||
      target.closest('.cdk-overlay-pane .nav-flyout-menu')
    );

    // Check if click is anywhere on the navbar (sidenav)
    const isOnNavbar = !!target.closest('.sidenav');

    // Close flyout if clicked on navbar but NOT on parent item or flyout
    if (isOnNavbar && !isOnParentItem && !isOnFlyout) {
      // Close all flyouts
      (['booking', 'ids', 'report'] as const).forEach((type) => {
        if (this.closeTimers[type]) {
          clearTimeout(this.closeTimers[type]);
          this.closeTimers[type] = null;
        }
        try {
          this.getTrigger(type)?.closeMenu();
        } catch {}
        this.hoverState[type] = { trigger: false, menu: false };
      });
      this.currentFlyout = null;
    }
  };

  private navigateToFirstAvailableSubmenu(type: string): void {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      switch (type) {
        case 'report':
          if (!this.hasAnyReportFeature()) {
            return;
          }
          // Navigate to first available report - members overview
          this.router.navigate(['/reports/members-overview']);
          break;
        case 'ids':
          // Navigate to first available ids - users
          this.router.navigate(['/ids/techno-user']);
          break;
      case 'booking':
        if (!this.hasAnyBookingFeature()) {
          return;
        }
        // Navigate to first available booking - schedule appointment
        if (
          this.permitedScheduleAppointment
        ) {
          this.router.navigate(['/booking/schedule-appointment']);
        } else if (
          this.permitedReservation
        ) {
          this.router.navigate(['/booking/reservation']);
        } else if (
          this.permitedResourceReservationSchedule
        ) {
          this.router.navigate(['/booking/resource-reservation-schedule']);
        } else if (
          this.permitedCustomerReservationSchedule
        ) {
          this.router.navigate(['/booking/customer-reservation-schedule']);
        } else if (
          this.permitedCalendarIndividualResource
        ) {
          this.router.navigate(['/booking/calendar-for-an-individual-resource']);
        } else if (
          this.permitedUser
        ) {
          this.router.navigate(['/booking/user']);
        } else if (
          this.permitedResourceOccupancy
        ) {
          this.router.navigate(['/booking/resource-occupancy']);
        } else if (
          this.permitedScheduleIndividualResource
        ) {
          this.router.navigate(['/booking/schedule-for-an-individual-resource']);
        } else if (
          this.permitedSettings
        ) {
          this.router.navigate(['/booking/app-configuration']);
        } else if (
          this.permitedWorkHours
        ) {
          this.router.navigate(['/booking/work-hours/new']);
        } else if (
          this.permitedResource
        ) {
          this.router.navigate(['/booking/resource']);
        } else if (
          this.permitedUtility
        ) {
          this.router.navigate(['/booking/utility']);
        } else if (
          this.permitedAsset
        ) {
          this.router.navigate(['/booking/asset']);
        } else if (
          this.permitedCustomerStatus
        ) {
          this.router.navigate(['/booking/customer-status']);
        }
        break;
    }
    }, 100);
  }

  isPaymentsActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl === '/payments' || currentUrl.startsWith('/card-extension/');
  }

  ngOnDestroy(): void {
    // Clean up the message count interval to prevent memory leaks
    if (this.messageCountInterval) {
      clearInterval(this.messageCountInterval);
    }

    // Clean up all subscriptions to prevent memory leaks
    this.subscriptions.forEach((subscription) => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  private hasAnyBookingFromRole(): boolean {
    return (
      this.permitedScheduleAppointment ||
      this.permitedReservation ||
      this.permitedResourceReservationSchedule ||
      this.permitedCustomerReservationSchedule ||
      this.permitedCalendarIndividualResource ||
      this.permitedUser ||
      this.permitedWorkHours ||
      this.permitedResourceOccupancy ||
      this.permitedScheduleIndividualResource ||
      this.permitedResource ||
      this.permitedUtility ||
      this.permitedAsset ||
      this.permitedCustomerStatus ||
      this.permitedSettings
    );
  }

  hasAnyBookingFeature(): boolean {
    // Show booking only if role grants at least one booking-related feature
    return this.hasAnyBookingFromRole();
  }

  hasAnyReportPermission(): boolean {
    const a = this.accountService;
    const hasPermission =
      a.canAccessFeature(FeaturesEnum.REPORT_USER_OVERVIEW) ||
      a.canAccessFeature(FeaturesEnum.REPORT_USER_CARD) ||
      a.canAccessFeature(FeaturesEnum.REPORT_ATTENDANCE_OVERVIEW) ||
      a.canAccessFeature(FeaturesEnum.REPORT_NEW_REGISTRATIONS) ||
      a.canAccessFeature(FeaturesEnum.REPORT_EXPIRED_MEMBERSHIPS) ||
      a.canAccessFeature(FeaturesEnum.REPORT_ATTENDANCE_DIAGRAM) ||
      a.canAccessFeature(FeaturesEnum.REPORT_GYM_VISITS) ||
      a.canAccessFeature(FeaturesEnum.REPORT_EXTERNAL_CENTERS_OBLIGATIONS) ||
      a.canAccessFeature(FeaturesEnum.REPORT_MEMBERSHIP_REVENUE) ||
      a.canAccessFeature(FeaturesEnum.REPORT_BAR) ||
      a.canAccessFeature(FeaturesEnum.REPORT_BUSINESS) ||
      a.canAccessFeature(FeaturesEnum.REPORT_BUSINESS_FOR_PERIOD) ||
      a.canAccessFeature(FeaturesEnum.TRAINERS_OVERVIEW) ||
      a.canAccessFeature(FeaturesEnum.TAXES_OVERVIEW);
    return hasPermission;
  }

  hasAnyReportFeature(): boolean {
    return this.hasAnyReportPermission();
  }

  hasIdsAccess(): boolean {
    try {
      return (
        this.accountService.hasAnyAuthority(['ROLE_ADMIN']) ||
        this.accountService.canAccessFeature(FeaturesEnum.USER_ADMINISTRATION)
      );
    } catch {
      return this.accountService.canAccessFeature(FeaturesEnum.USER_ADMINISTRATION);
    }
  }

  private evaluateOpenDoorAvailability(): void {
    this.canOpenDoor = false;
    this.availableDevices = [];
    if (!this.benefitAccessControl) {
      return;
    }
    const locId = this.localStorageService.retrieve('location');
    if (!locId) {
      return;
    }
    this.locationsService.getDevicesByLocationId(locId).subscribe({
      next: (res) => {
        const devices = res.body || [];
        this.availableDevices = devices;
        this.canOpenDoor = devices.length > 0;
      },
      error: () => {
        this.availableDevices = [];
        this.canOpenDoor = false;
      },
    });
  }
}
